import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Users, UserCheck, UserX, UserPlus, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { getTenantSlug, apiUpdateTeamSkills, apiUpdateTeamAllocations, API_BASE } from "@/lib/api";
import { SwimlanesWidget } from "@/components/team/SwimlanesWidget";
import { CapacityAllocationWidget } from "@/components/team/CapacityAllocationWidget";
import { SkillMatrixWidget } from "@/components/team/SkillMatrixWidget";
import { ForecastWidget } from "@/components/team/ForecastWidget";
import { AddEmployeeDialog } from "@/components/team/AddEmployeeDialog";
import { OffboardingRequestsWidget } from "@/components/team/OffboardingRequestsWidget";
import { type TeamMember, type ProjectAllocation } from "@/components/team/mockData";
import { CardGridSkeleton } from "@/components/PageSkeleton";

// ── Page ──────────────────────────────────────────────────────────────────────

export default function TeamOverview() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const isLineManager = !!user?.isLineManager;

  const [teamName, setTeamName] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    if (!isLineManager) return;
    const load = async () => {
      try {
        const slug = getTenantSlug();
        const token = localStorage.getItem(`besthr_${slug}_token`) ?? "";
        const res = await fetch(`${API_BASE}/api/teams/my-team?_t=${encodeURIComponent(slug)}`, {
          cache: "no-store" as RequestCache,
          headers: {
            "x-user-id": user?.id?.toString() ?? "",
            "x-tenant": slug,
            ...(token ? { "Authorization": `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) return;
        const data = await res.json();
        setTeamName(data.teamName || null);
        // Map API members → TeamMember shape (fill defaults for mock fields)
        const mapped: TeamMember[] = (data.members ?? []).map((m: any) => ({
          id: Number(m.id),
          name: m.name,
          initials: m.initials ?? m.name?.slice(0, 2).toUpperCase() ?? "??",
          avatarColor: m.avatarColor ?? "#6366f1",
          role: m.jobTitle ?? "",
          department: m.department ?? "",
          reportsTo: m.reportsToId ?? null,
          lineManagerId: m.reportsToId ?? null,
          skills: m.skills ?? {},
          capacityPercent: m.capacityPercent ?? 80,
          burnoutFactors: m.burnoutFactors ?? { leaveBalanceDays: 20, overtimeDays: 0, consecutiveWorkMonths: 0 },
          startDate: m.startDate ?? new Date().toISOString().slice(0, 10),
          isNewHire: m.isNewHire ?? false,
          status: (m.status as any) || "active",
          allocations: m.allocations ?? [],
        }));
        setTeamMembers(mapped);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isLineManager, user?.id]);

  const currentTeamIds = useMemo(
    () => new Set(teamMembers.map((m) => m.id)),
    [teamMembers],
  );

  function handleAddMember(member: TeamMember) {
    setTeamMembers((prev) => [...prev, member]);
  }

  async function handleSaveSkills(draft: Record<number, Record<string, number>>) {
    try {
      await apiUpdateTeamSkills(draft);
      setTeamMembers((prev) =>
        prev.map((m) => {
          if (draft[m.id]) return { ...m, skills: draft[m.id] };
          return m;
        }),
      );
    } catch (err) {
      console.error("Failed to update skills:", err);
      throw err;
    }
  }

  async function handleSaveAlloc(memberId: number, allocs: ProjectAllocation[]) {
    try {
      await apiUpdateTeamAllocations(memberId, allocs);
      
      const newCapacity = allocs.reduce((sum, a) => sum + (a.percent || 0), 0);
      
      setTeamMembers((prev) =>
        prev.map((m) => {
          if (m.id === memberId) {
            return {
              ...m,
              allocations: allocs,
              capacityPercent: newCapacity,
            };
          }
          return m;
        }),
      );
    } catch (err) {
      console.error("Failed to update allocations:", err);
      throw err;
    }
  }

  // Derived KPI stats
  const totalHeadcount = teamMembers.length;
  const activeToday = teamMembers.filter(
    (m) => m.status === "active" || m.status === "remote",
  ).length;
  const onLeaveToday = teamMembers.filter((m) => m.status === "on_leave").length;
  const newHiresCount = teamMembers.filter((m) => m.isNewHire).length;

  // ── Access guard — only line managers ──────────────────────────────────────
  if (!isLineManager) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-32 text-center gap-4">
          <ShieldOff className="h-12 w-12 text-muted-foreground opacity-40" />
          <h2 className="text-lg font-semibold text-foreground">
            {t("teamOverview.accessDeniedTitle")}
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {t("teamOverview.accessDeniedDesc")}
          </p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* ── Page Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
              <Users className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {teamName || t("teamOverview.title")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("teamOverview.subtitle")}
              </p>
            </div>
          </div>

          <Button
            className="gradient-brand text-primary-foreground text-sm gap-2"
            onClick={() => setShowAddDialog(true)}
          >
            <UserPlus className="h-4 w-4" />
            {t("teamOverview.addEmployee")}
          </Button>
        </div>

        {/* ── KPI Strip ────────────────────────────────────────────── */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border/60">
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
                <Users className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none tabular-nums">
                  {loading ? "—" : totalHeadcount}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("teamOverview.totalHeadcount")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <UserCheck className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none tabular-nums">
                  {loading ? "—" : activeToday}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("teamOverview.activeToday")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                <UserX className="h-4 w-4 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none tabular-nums">
                  {loading ? "—" : onLeaveToday}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("teamOverview.onLeaveToday")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-sky-500/10 flex items-center justify-center shrink-0">
                <UserPlus className="h-4 w-4 text-sky-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none tabular-nums">
                  {loading ? "—" : newHiresCount}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("teamOverview.newHires")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            <CardGridSkeleton count={1} className="grid grid-cols-1 gap-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CardGridSkeleton count={2} className="grid grid-cols-1 md:grid-cols-2 gap-4" />
            </div>
            <CardGridSkeleton count={1} className="grid grid-cols-1 gap-4" />
          </div>
        ) : (
          <>
            {/* ── Full-width: Swimlanes ─────────────────────────────────── */}
            <SwimlanesWidget members={teamMembers} isHR={false} />

            {/* ── Bento Grid Row: Capacity + Forecast ───────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CapacityAllocationWidget
                members={teamMembers}
                isHR={false}
                isLineManager={true}
                onSaveAlloc={handleSaveAlloc}
              />
              <ForecastWidget members={teamMembers} isHR={false} />
            </div>

            {/* ── Full-width: Skill Matrix ──────────────────────────────── */}
            <SkillMatrixWidget
              members={teamMembers}
              isHR={false}
              isLineManager={true}
              onSaveSkills={handleSaveSkills}
            />
          </>
        )}

        {/* ── Team Exit Requests (bottom) ───────────────────────────── */}
        <OffboardingRequestsWidget />
      </div>

      {/* ── Add / Manage Employee Dialog ─────────────────────────── */}
      <AddEmployeeDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        currentTeamIds={currentTeamIds}
        isHR={false}
        isLineManager={true}
        onAddMember={handleAddMember}
      />
    </AppLayout>
  );
}
